import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { db, isConfigured } from '../firebaseConfig';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, orderBy, query } from 'firebase/firestore';

export default function Services() {
  const [services, setServices] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });

  useEffect(() => {
    if (isConfigured) {
      fetchServices();
    }
  }, []);

  const fetchServices = async () => {
    if (!isConfigured) return;
    try {
      const q = query(collection(db, 'services'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const servicesList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setServices(servicesList);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isConfigured) {
      alert('Firebase is not configured. Please set up your Firebase configuration first.');
      return;
    }
    
    try {
      if (editingService) {
        await updateDoc(doc(db, 'services', editingService.id), {
          ...formData,
          updatedAt: new Date().toISOString()
        });
        alert('Service updated successfully!');
      } else {
        await addDoc(collection(db, 'services'), {
          ...formData,
          createdAt: new Date().toISOString()
        });
        alert('Service added successfully!');
      }
      
      setFormData({
        title: '',
        description: ''
      });
      setShowAddForm(false);
      setEditingService(null);
      fetchServices();
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Error saving service. Please try again.');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title || '',
      description: service.description || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await deleteDoc(doc(db, 'services', id));
        alert('Service deleted successfully!');
        fetchServices();
      } catch (error) {
        console.error('Error deleting service:', error);
        alert('Error deleting service. Please try again.');
      }
    }
  };

  const cancelEdit = () => {
    setShowAddForm(false);
    setEditingService(null);
    setFormData({
      title: '',
      description: ''
    });
  };

  return (
    <div className='flex min-h-screen bg-[#FFB7CE]'>
      <Sidebar />
      <div className='flex-1 p-4 pt-20 lg:pt-4 lg:pl-8'>
        <div className='max-w-6xl mx-auto'>
          {!isConfigured && (
            <div className='bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6 rounded'>
              <p className='font-bold'>⚠️ Firebase Not Configured</p>
              <p className='text-sm mt-2'>
                Please set up your Firebase configuration in <code className='bg-yellow-200 px-2 py-1 rounded'>src/firebaseConfig.js</code> to enable database functionality.
              </p>
            </div>
          )}
          
          <div className='flex justify-between items-center mb-6'>
            <h1 className='text-3xl font-bold text-white'>SERVICES</h1>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className='bg-[#89CFF0] hover:bg-[#6BB8E0] text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200'
              disabled={!isConfigured}
            >
              {showAddForm ? 'CANCEL' : 'ADD'}
            </button>
          </div>

          {showAddForm && (
            <div className='bg-white rounded-lg p-6 mb-6 shadow-lg'>
              <h2 className='text-2xl font-bold text-[#89CFF0] mb-6'>
                {editingService ? 'EDIT SERVICE' : 'ADD SERVICE'}
              </h2>
              <div className='space-y-4'>
                <div>
                  <label className='block text-gray-700 font-medium mb-2'>TITLE:</label>
                  <input
                    type='text'
                    name='title'
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    placeholder='e.g., Web Development'
                    className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89CFF0]'
                  />
                </div>

                <div>
                  <label className='block text-gray-700 font-medium mb-2'>DESCRIPTION:</label>
                  <textarea
                    name='description'
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows='5'
                    placeholder='Describe the service you offer...'
                    className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89CFF0]'
                  />
                </div>

                <div className='flex gap-4'>
                  <button
                    onClick={handleSubmit}
                    className='bg-[#89CFF0] hover:bg-[#6BB8E0] text-white px-8 py-2 rounded-lg font-medium transition-colors duration-200'
                  >
                    {editingService ? 'UPDATE' : 'ADD'}
                  </button>
                  {editingService && (
                    <button
                      onClick={cancelEdit}
                      className='bg-gray-400 hover:bg-gray-500 text-white px-8 py-2 rounded-lg font-medium transition-colors duration-200'
                    >
                      CANCEL
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className='bg-white rounded-lg overflow-hidden shadow-lg'>
            <table className='w-full'>
              <thead className='bg-[#FFB7CE]'>
                <tr>
                  <th className='px-4 py-3 text-left text-white font-bold w-1/4'>TITLE</th>
                  <th className='px-4 py-3 text-left text-white font-bold w-1/2'>DESCRIPTION</th>
                  <th className='px-4 py-3 text-left text-white font-bold w-1/4'>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {services.length === 0 ? (
                  <tr>
                    <td colSpan='3' className='px-4 py-8 text-center text-gray-500'>
                      No services yet. Click ADD to create your first service.
                    </td>
                  </tr>
                ) : (
                  services.map(service => (
                    <tr key={service.id} className='border-b hover:bg-gray-50'>
                      <td className='px-4 py-3 font-medium text-gray-800'>{service.title}</td>
                      <td className='px-4 py-3 text-gray-600 text-sm'>
                        <p className='line-clamp-3'>{service.description}</p>
                      </td>
                      <td className='px-4 py-3'>
                        <div className='flex gap-2'>
                          <button
                            onClick={() => handleEdit(service)}
                            className='bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors duration-200'
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(service.id)}
                            className='bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition-colors duration-200'
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}